import React, {useCallback, useContext, useEffect, useLayoutEffect, useRef} from "react";
import {DataGridMiscContext} from "../../../index";
import {useCombinedRefs, useIsMounted, useResizeObserver} from "../../../../base/ui";
import DataGridObserversService from "../../../core/services/observers";
import debounce from "lodash.debounce";

const DataGridEmptyContent = ({loading = false}) => {
    const {EmptyContentComponent} = useContext(DataGridMiscContext);
    const {rect, ref: resizeObserverRef} = useResizeObserver();
    /**@type {React.MutableRefObject<HTMLTableElement>}*/
    const containerRef = useRef();
    const ref = useCombinedRefs(resizeObserverRef, containerRef);
    const isMounted = useIsMounted();

    /**
     * As soon as the component unmounts:
     * - removes the injected height of the [tbody] element of the table.
     */
    useLayoutEffect(() => {
        const containerElement = containerRef.current?.parentElement;
        const tBodyElement = containerElement?.querySelector('tbody');
        const tableBodyHeight = tBodyElement.style.height;
        return () => {
            tBodyElement.style.height = tableBodyHeight;
        }
    }, [])


    /**
     * With each change in the height of the container element:
     * - sets the height of the [tbody] element of the data-grid table so that the table can expand according to the
     * height of the given empty-content-component.
     */
    useLayoutEffect(() => {
        const containerElement = containerRef.current?.parentElement;
        const layoutElement = containerElement?.querySelector('.data-grid-layout');
        const tBodyElement = containerElement?.querySelector('tbody');
        const tHeaderElement = containerElement?.querySelector('thead');
        const toolbarElement = containerElement?.querySelector('div.data-grid-toolbar');

        if (!layoutElement || !tBodyElement || !tHeaderElement)
            return;

        const containerElementStyles = getComputedStyle(containerElement);
        const layoutSize = layoutElement.getBoundingClientRect();
        const tableHeaderSize = tHeaderElement.getBoundingClientRect();
        const toolbarHeight = toolbarElement?.getBoundingClientRect()?.height ?? 0;

        tBodyElement.style.height = `${rect?.height ?? 0}px`;

        containerRef.current.style.left = containerElementStyles.paddingLeft;
        containerRef.current.style.right = containerElementStyles.paddingRight;
        containerRef.current.style.top = `${tableHeaderSize.height + toolbarHeight}px`;
        containerRef.current.style.width = `${layoutSize?.width}px`;
        containerRef.current.style.marginTop = getComputedStyle(layoutElement).marginTop;

    }, [rect?.height, containerRef]);

    /**
     * Syncs the size of the loading component's container with the provided constraints:
     * @type {(function(*, *): void)}
     */
    const syncSize = useCallback((entries) => {
        window.requestAnimationFrame(() => {
            if (!entries?.length || !isMounted()) {
                return;
            }
            const entry = entries[0];
            const rect = entry.contentRect ?? entry?.getBoundingClientRect();
            if (containerRef.current) {
                containerRef.current.style.width = (rect.width ?? 0) + 'px';
            }
        })
    }, [isMounted])

    /**
     * With each change in the [containerRef], [rowsLength] value and [visibleRows] value:
     *
     * - attaches an observer that would sync the size of the loading component's container.
     */
    useEffect(() => {
        const containerElement = containerRef.current?.parentElement;
        const layoutElement = containerElement?.querySelector('.data-grid-layout');
        if (!layoutElement)
            return;
        const throttledSyncSize = debounce((entries, observer) => syncSize(entries, observer), 0);
        const observer = DataGridObserversService.newResizeObserver(throttledSyncSize);
        DataGridObserversService.observeResizeObserver(observer, layoutElement);
        throttledSyncSize([layoutElement], observer)
        return () => DataGridObserversService.unobserveResizeObserver(observer, layoutElement);
    }, [containerRef])


    return (
        <>
            <div ref={ref} className={'data-grid-empty-content-container'}>
                {
                    !!EmptyContentComponent && React.isValidElement(EmptyContentComponent)
                        ? React.cloneElement(EmptyContentComponent)
                        : <DefaultDataGridEmptyContent loading={loading}/>
                }
            </div>

        </>
    );
}

const DefaultDataGridEmptyContent = ({loading}) => {

    return (
        <>
            <div className={'default-data-grid-empty-content'}>
                {loading ? "" : "No results Found"}
            </div>
        </>
    )
}

export default DataGridEmptyContent;
